import { useCallback, memo } from 'react';
import { getTransferrableProps } from '../utils/getTransferrableProps';
import { parseNumericValue } from '../utils/parseNumericValue';
import { isProbability } from 'customer-data-objects/property/PropertyIdentifier';
import useComponentMountMetric from '../metrics/useComponentMountMetric';
import { useProperty } from '../v2/hooks/useProperty';
import { usePropertyValue } from '../v2/hooks/usePropertyValue';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { PercentageInput } from 'foundations-components/transitional/components/PercentageInput';
import { jsx as _jsx } from "react/jsx-runtime";
const PERCENT_INPUT_PRECISION = 10;

/**
 * This was previously written inline as `String(Number(percentString) / 100)`
 * The new approach adds toFixed in order to avoid floating point precision issues
 * For example 1.3/100 = 0.013000000000000001
 * But (1.3/100).toFixed(4) = 0.0130
 * This is important because some properties use a "Limit number of decimal places" validation
 * so the extra floating point noise can cause unexpected validation errors
 */
const convertPercentToDecimalString = percentString => {
  const decimalValue = Number(percentString) / 100;
  // We add 2 to the PERCENT_INPUT_PRECISION because we are representing the percentage as a decimal
  // For example 0.05% is represented as 0.0005
  const decimalFixed = decimalValue.toFixed(PERCENT_INPUT_PRECISION + 2);
  const decimalFixedWithoutTrailingZeros = Number(decimalFixed);
  const decimalString = String(decimalFixedWithoutTrailingZeros);
  return decimalString;
};
// Values of properties with `numberDisplayHint=probability` are restricted to the range `[0, 1]`.
// Values of properties with `numberDisplayHint=percentage` are not restricted.
const PROBABILITY_ALLOWED_RANGE_PROPS = {
  min: 0,
  max: 100
};
const PropertyInputPercentage = props => {
  const {
    onChange,
    inputRef
  } = props;
  const property = useProperty();
  const value = usePropertyValue();
  useComponentMountMetric('PropertyInputPercentage');
  const handleChange = useCallback(evt => {
    if (typeof onChange === 'function') {
      // UIPercentageInput's onChange sends `number | null`; convert to `string | null`
      onChange(evt.target.value == null ? evt : syntheticEvent(convertPercentToDecimalString(evt.target.value)));
    }
  }, [onChange]);
  const numericValue = parseNumericValue(value);
  const percentageValue = numericValue === null ? null :
  // We want to retrieve the percentage value from the decimal value but fix
  // it to our precision limit to avoid floating point math issues with multiplication
  // see: https://issues.hubspotcentral.com/browse/CRM-54436 for past problems
  Number((numericValue * 100).toFixed(PERCENT_INPUT_PRECISION));
  const allowedRangeProps = isProbability(property) ? PROBABILITY_ALLOWED_RANGE_PROPS : undefined;
  return /*#__PURE__*/_jsx(PercentageInput, Object.assign({}, getTransferrableProps(props), {
    onChange: handleChange,
    inputRef: inputRef
    // @ts-expect-error UIPercentageInput propTypes allow 'null', but TS types do not; see https://git.hubteam.com/HubSpot/UIComponents/issues/10822
    ,
    value: percentageValue
  }, allowedRangeProps, {
    precision: PERCENT_INPUT_PRECISION
  }));
};
export default /*#__PURE__*/memo(PropertyInputPercentage);